import type { KeyboardEvent } from 'react'
import { useState, useEffect } from 'react'
import {
  faChevronDown,
  faChevronUp,
  faExclamationCircle,
  faSearch
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { isLanguageSpanish } from 'helpers/language'
import useCustomTranslation from 'hooks/useCustomTranslation'
import {
  Chevron,
  CloseButton,
  ErrorMessage,
  Header,
  ItemsSelected,
  NoOptions,
  OptionsContainer,
  OptionsWrapper,
  PlaceHolder,
  SearchContainer,
  SearchInput,
  SelectedWrapper,
  StyledCheckbox,
  Unselect
} from './MultiSelect.styles'
import {
  MultiSelectOption,
  MultiSelectHandleChange,
  MultiSelectHeaderParams,
  OptionParams,
  OptionsContainerParams,
  SelectedMapProps,
  NoSelectedMapProps
} from './typings'

export const focusBack = (
  isOpen: boolean,
  searchRef: React.RefObject<HTMLInputElement>
): void => {
  setTimeout(() => {
    return isOpen && searchRef?.current?.focus()
  }, 100)
}

export const cleanOptions = (
  isOpen: boolean,
  searchRef: React.RefObject<HTMLInputElement>,
  onChange: (options: MultiSelectOption[]) => void
): void => {
  onChange([])
  focusBack(isOpen, searchRef)
}

export const MultiSelectCheckbox = ({
  uniqueKey,
  onChange,
  label,
  isChecked = false
}: OptionParams): JSX.Element => (
  <StyledCheckbox
    key={uniqueKey}
    id={uniqueKey}
    isChecked={isChecked}
    onChange={onChange}
  >
    {label || ''}
  </StyledCheckbox>
)

export const MultiSelectHeader = ({
  error,
  disabled,
  openCloseOptions,
  testId,
  isOpen,
  isThereOptionsSelected,
  textSelected,
  placeholder,
  searchRef,
  onChange
}: MultiSelectHeaderParams): JSX.Element => {
  const text = isThereOptionsSelected ? (
    <span title={textSelected}>{textSelected}</span>
  ) : (
    <PlaceHolder>{placeholder}</PlaceHolder>
  )
  return (
    <Header
      error={error}
      disabled={disabled}
      tabIndex={0}
      onKeyPress={(e: KeyboardEvent<HTMLInputElement>) =>
        e.key === 'Enter' && openCloseOptions()
      }
      data-testid={testId}
    >
      {!isOpen ? (
        <SelectedWrapper>
          <ItemsSelected disabled={disabled} onClick={() => openCloseOptions()}>
            {text}
          </ItemsSelected>
          {isThereOptionsSelected && (
            <CloseButton
              onClick={() => cleanOptions(isOpen, searchRef, onChange)}
            >
              X
            </CloseButton>
          )}
        </SelectedWrapper>
      ) : (
        <PlaceHolder>{placeholder}</PlaceHolder>
      )}
      <Chevron
        error={error}
        icon={isOpen ? faChevronUp : faChevronDown}
        onClick={() => openCloseOptions()}
        data-testid={`${isOpen ? 'up' : 'down'}`}
      />
    </Header>
  )
}

export const ErrorContainer = ({ error }: { error: string }): JSX.Element => (
  <ErrorMessage data-error="true">
    <FontAwesomeIcon
      icon={faExclamationCircle}
      style={{ marginRight: '5px', fontSize: '1.3em' }}
    />
    {error}
  </ErrorMessage>
)

export const languageLabel = (option: MultiSelectOption): string => {
  return isLanguageSpanish ? option.spanishLabel : option.englishLabel
}

export const NoSelectedMap = ({
  option,
  id,
  selectedOptions,
  onChange
}: NoSelectedMapProps): JSX.Element | null => {
  const key = `unselected_${option.value}_${id}`
  const checked =
    selectedOptions?.find(item => item.value === option.value) !== undefined
  return !checked ? (
    <MultiSelectCheckbox
      uniqueKey={key}
      label={languageLabel(option)}
      onChange={onChange}
    />
  ) : null
}

const SelectedMap = ({
  option,
  id,
  options,
  onChange
}: SelectedMapProps): JSX.Element => {
  const key = `selected_${option}_${id}`
  const selectedOption = options.find(
    (localOption: MultiSelectOption) => localOption.value === option.value
  )
  return (
    <MultiSelectCheckbox
      uniqueKey={key}
      label={languageLabel(selectedOption as MultiSelectOption)}
      onChange={onChange}
      isChecked
    />
  )
}

export const handleChange = ({
  selected,
  checked,
  selectedOptions,
  onChange,
  isOpen,
  searchRef
}: MultiSelectHandleChange): void => {
  let newOptions: MultiSelectOption[] = []
  if (checked) {
    newOptions = [...selectedOptions, selected]
  } else {
    newOptions = selectedOptions.filter(
      option => option.value !== selected.value
    )
  }
  onChange(newOptions)
  focusBack(isOpen, searchRef)
}

export const MultiSelectOptionsContainer = ({
  handleWrapperOnBlur,
  handleWrapperOnFocus,
  isOpen,
  searchRef,
  setIsOpen,
  selectedOptions,
  name,
  options,
  isAllSelected,
  onChange
}: OptionsContainerParams): JSX.Element => {
  const t = useCustomTranslation('form.request.multiselect')

  const [searchText, setSearchText] = useState('')
  const [localOptions, setLocalOptions] = useState(options)
  const isThereOptions = localOptions.length > 0

  useEffect(() => {
    const localOptionsFiltered = options.filter(item =>
      languageLabel(item).toLowerCase().includes(searchText.toLowerCase())
    )
    setLocalOptions(localOptionsFiltered)
  }, [searchText, options])

  return (
    <OptionsWrapper
      onBlur={handleWrapperOnBlur}
      onFocus={handleWrapperOnFocus}
      tabIndex={0}
      data-testid="opencontainer"
    >
      <SearchContainer>
        <FontAwesomeIcon
          icon={faSearch}
          style={{ position: 'absolute', left: '12px', color: '#34657F' }}
        />
        <SearchInput
          type="text"
          ref={searchRef}
          placeholder={t('search') || ''}
          onClick={() => setIsOpen(true)}
          onChange={e => setSearchText(e.target.value)}
          value={searchText}
        />
      </SearchContainer>
      {selectedOptions && !!selectedOptions.length && (
        <OptionsContainer>
          <Unselect
            id={`unselect-all-${name}`}
            onClick={() => {
              cleanOptions(isOpen, searchRef, onChange)
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                cleanOptions(isOpen, searchRef, onChange)
              }
            }}
            role="button"
          >
            {t('unselect')}
          </Unselect>
          {selectedOptions.map((option, idx) => (
            <SelectedMap
              key={`sel_${option.value}`}
              option={option}
              id={idx}
              options={options}
              onChange={() => {
                handleChange({
                  selected: option,
                  checked: false,
                  selectedOptions,
                  onChange,
                  isOpen,
                  searchRef
                })
                setIsOpen(true)
              }}
            />
          ))}
        </OptionsContainer>
      )}
      <OptionsContainer>
        {isThereOptions ? (
          !isAllSelected && (
            <StyledCheckbox
              id={`select-all-${name}`}
              isChecked={isAllSelected}
              onChange={() => {
                onChange(isAllSelected ? [] : options.map(item => item))
              }}
            >
              {t('all')}
            </StyledCheckbox>
          )
        ) : (
          <NoOptions>{t('noOptions')}</NoOptions>
        )}
        {localOptions &&
          localOptions.map((option, idx) => {
            return (
              <NoSelectedMap
                key={`unsel_${option.value}`}
                option={option}
                id={idx}
                selectedOptions={selectedOptions}
                onChange={() => {
                  handleChange({
                    selected: option,
                    checked: true,
                    selectedOptions,
                    onChange,
                    isOpen,
                    searchRef
                  })
                  setIsOpen(true)
                }}
              />
            )
          })}
      </OptionsContainer>
    </OptionsWrapper>
  )
}
